<script setup lang="ts">
import { computed, useId } from 'vue'

// Checkbox (or switch, with `toggle`) with a label and an optional description.
const props = defineProps<{
  label: string
  description?: string
  disabled?: boolean
  toggle?: boolean
  id?: string
}>()

const model = defineModel<boolean>({ default: false })

const generatedId = useId()
const inputId = computed(() => props.id ?? `check-${generatedId}`)
</script>

<template>
  <label :for="inputId" class="ui-check" :class="{ 'ui-check--disabled': disabled, 'ui-check--toggle': toggle }">
    <input
      :id="inputId"
      v-model="model"
      type="checkbox"
      class="ui-check__input"
      :role="toggle ? 'switch' : undefined"
      :disabled="disabled"
    />
    <span class="ui-check__box" aria-hidden="true">
      <svg v-if="!toggle" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
    </span>
    <span class="ui-check__text">
      <span class="ui-check__label">{{ label }}</span>
      <span v-if="description" class="ui-check__description">{{ description }}</span>
    </span>
  </label>
</template>

<style scoped>
.ui-check {
  display: inline-flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  position: relative;
}
.ui-check--disabled {
  opacity: 0.55;
  cursor: default;
}
.ui-check__input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
}
.ui-check__box {
  flex: none;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  border-radius: var(--radius-xs);
  border: 1px solid var(--muted-2);
  background: var(--surface-card);
  color: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background var(--duration-fast) var(--ease-standard);
}
.ui-check__input:checked + .ui-check__box {
  background: var(--accent);
  border-color: var(--accent);
  color: var(--on-accent);
}
.ui-check__input:focus-visible + .ui-check__box {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
/* Switch (15/10/2026: symmetric): a 34 × 20 track without a border and a 16 px knob 2 px from every edge, off on the
   left and on on the right, always centred on the height; the switch is centred on the first line of the label. */
.ui-check--toggle .ui-check__label {
  line-height: var(--type-body-line);
}
.ui-check--toggle .ui-check__box {
  box-sizing: border-box;
  width: 34px;
  height: 20px;
  margin-top: 0;
  border: none;
  border-radius: 999px;
  position: relative;
  background: var(--system-gray-3);
}
.ui-check--toggle .ui-check__box::after {
  content: '';
  position: absolute;
  left: 2px;
  top: 2px;
  width: 16px;
  height: 16px;
  border-radius: 999px;
  background: #fff;
  box-shadow: var(--shadow-knob);
  transition: transform var(--duration-fast) var(--ease-standard);
}
.ui-check--toggle .ui-check__input:checked + .ui-check__box::after {
  transform: translateX(14px);
}
.ui-check__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.ui-check__label {
  font-size: var(--type-body-size);
}
.ui-check__description {
  font-size: var(--type-footnote-size);
  color: var(--text-secondary);
}
</style>
